import {
  MuscleGroup,
  MuscleGroupCategory,
  MUSCLE_GROUP_TO_CATEGORY,
  MUSCLE_GROUP_CATEGORY_LABELS,
  MUSCLE_GROUP_CATEGORY_COLORS,
  MUSCLE_GROUP_CATEGORY_ORDER,
} from "../models/muscleGroups";
import {
  IVolumeExercise,
  exerciseCompletedSets,
  computeTotalCompletedSets,
} from "./workoutVolume";

export interface IMuscleSplitSlice {
  category: MuscleGroupCategory;
  label: string;
  color: string;
  /** Working-set count accumulated for this category (see {@link computeMuscleSplit}). */
  sets: number;
  /** Share of the workout, 0–100, rounded for display. */
  percent: number;
}

export interface ISplitExerciseInput extends IVolumeExercise {
  muscleGroups: MuscleGroup[];
}

const distinctCategories = (groups: MuscleGroup[]): MuscleGroupCategory[] => {
  const set = new Set<MuscleGroupCategory>();
  for (const group of groups ?? []) {
    const category = MUSCLE_GROUP_TO_CATEGORY[group];
    if (category !== undefined) set.add(category);
  }
  return [...set];
};

/**
 * Rolls a workout's exercises up into a per-category muscle split.
 *
 * Each exercise is weighted by its number of working sets — not kg×reps tonnage — and
 * that weight is spread equally across the distinct high-level categories it targets.
 * Set count is the metric sports science (Renaissance Periodization's "sets per muscle
 * group" volume landmarks) and trackers like Hevy use to express how training is
 * *distributed* across muscles: every working set is a roughly comparable dose of
 * stimulus, so a bodyweight set (Chin Ups, Hip Thrust, planks) counts the same as a
 * loaded one instead of vanishing just because it carries no kg. Tonnage is still shown
 * separately as the "Общ обем" badge — that's the progression metric, not the balance one.
 *
 * Once anything in the workout is checked off we count only completed sets, so the split
 * reflects what was actually done; while everything is still unchecked (a fresh plan)
 * we count planned sets so the breakdown is visible as the workout is being built.
 *
 * Exercises with no muscle group selected are folded into a catch-all "Други" (Other)
 * bucket so the whole workout is accounted for; that bucket is always pinned to the end.
 *
 * Returns slices sorted by share (largest first, Other last), summing to ~100%.
 */
export const computeMuscleSplit = (
  exercises: ISplitExerciseInput[],
): IMuscleSplitSlice[] => {
  const all = exercises ?? [];
  if (all.length === 0) return [];

  // Once anything is checked off, the split reflects completed work; until then we
  // weight by planned sets so the breakdown is visible while the plan is built.
  const anyCompleted = computeTotalCompletedSets(all) > 0;
  const exerciseSetCount = (exercise: ISplitExerciseInput): number =>
    anyCompleted ? exerciseCompletedSets(exercise) : exercise.sets?.length ?? 0;

  const byCategory = new Map<MuscleGroupCategory, number>();
  for (const exercise of all) {
    const sets = exerciseSetCount(exercise);
    if (sets <= 0) continue;
    const categories = distinctCategories(exercise.muscleGroups);
    if (categories.length === 0) {
      // Untagged exercise → fold its sets into the "Other" bucket so it still
      // shows up in the split instead of silently vanishing.
      byCategory.set(
        MuscleGroupCategory.Other,
        (byCategory.get(MuscleGroupCategory.Other) ?? 0) + sets,
      );
      continue;
    }
    const share = sets / categories.length;
    for (const category of categories) {
      byCategory.set(category, (byCategory.get(category) ?? 0) + share);
    }
  }

  const total = [...byCategory.values()].reduce((a, b) => a + b, 0);
  if (total <= 0) return [];

  const slices: IMuscleSplitSlice[] = [...byCategory.entries()].map(
    ([category, sets]) => ({
      category,
      label: MUSCLE_GROUP_CATEGORY_LABELS[category],
      color: MUSCLE_GROUP_CATEGORY_COLORS[category],
      sets,
      percent: Math.round((sets / total) * 100),
    }),
  );

  slices.sort((a, b) => {
    // "Други" is a catch-all, not a real muscle — keep it pinned at the bottom
    // regardless of how many untagged sets there are.
    const aOther = a.category === MuscleGroupCategory.Other;
    const bOther = b.category === MuscleGroupCategory.Other;
    if (aOther !== bOther) return aOther ? 1 : -1;
    if (b.sets !== a.sets) return b.sets - a.sets;
    return (
      MUSCLE_GROUP_CATEGORY_ORDER.indexOf(a.category) -
      MUSCLE_GROUP_CATEGORY_ORDER.indexOf(b.category)
    );
  });

  return slices;
};
